import * as wjcCore from '@mescius/wijmo';
import * as wjcGrid from '@mescius/wijmo.grid';
import * as wjcOlap from '@mescius/wijmo.olap';
import {AfterViewChecked, Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {DataService, OrientDBCollectionView} from '@cosbeni/database';
import {ActivePromotionConfig} from '../config/active-promotion.config';
import {StimulsoftFontService} from '../services/stimulsoft-font.service';
import {KobeacService} from './kobeac.service';
import {KobeacItemGridBase} from './item-grid.base';

declare var Stimulsoft: any;

/** kobe-ac 精算書 (旧 /auction/summary) */
@Component({
    standalone: false,
    selector: 'kobeac-summary',
    templateUrl: 'summary.component.html',
})
export class KobeacSummaryComponent extends KobeacItemGridBase implements OnInit, AfterViewChecked {
    @ViewChild('summaryGrid') summaryGrid: wjcGrid.FlexGrid;
    @ViewChild('itemGrid') itemGrid: wjcGrid.FlexGrid;
    @ViewChild('detailGrid') detailGrid: wjcGrid.FlexGrid;

    detail: OrientDBCollectionView;
    summaries: wjcCore.CollectionView = new wjcCore.CollectionView([]);
    engine: wjcOlap.PivotEngine;
    entrance = 0;
    entrance2 = 0;
    customer: any = null;
    private viewer: any;

    /** 統合画面 (kobeac-ledger-home) 内に埋め込むと true。自前の日付セレクタを隠し、
     *  オークションは親から auctionRid で受け取る。単独ルートでは false のまま。 */
    @Input() embedded = false;
    /** 親から選択された顧客 (@rid) を通知する (統合画面が請求編集へ渡す)。 */
    @Output() customerSelected = new EventEmitter<string>();
    /** auctions 未ロード時に受けた auctionRid を保持し、ngOnInit で適用する。 */
    private pendingAuctionRid: string | null = null;

    @Input() set auctionRid(rid: string) {
        if (this.auctions && this.auctions.length) {
            this.applyAuction(rid);
        } else {
            this.pendingAuctionRid = rid;
        }
    }

    /** 指定 rid のオークションを選択し集計し直す (現在と同じなら何もしない)。 */
    private applyAuction(rid: string): void {
        if (!rid) {
            return;
        }
        if (wjcCore.isObject(this.current) && this.current['@rid'] === rid) {
            return;
        }
        const found = this.auctions.filter((a) => a['@rid'] === rid);
        if (found.length) {
            this.current = found[0];
            this.customer = null;
            this.onAuctionChanged();
        }
    }

    constructor(dataService: DataService, kobeac: KobeacService, private fontService: StimulsoftFontService) {
        super(dataService, kobeac);
        this.detail = new OrientDBCollectionView(dataService, ActivePromotionConfig.DATABASE,
            'SELECT FROM AC_Item', 'AC_Item', '', 0, 5000, true, true, false);
        this.detail.url = ActivePromotionConfig.SERVER_URL;
        this.detail.tokenName = ActivePromotionConfig.DATABASE + '_token';
        this.detail.fetchPlan = '*:0';
        this.detail.itemsSourceFunction = (rows: any[]) => this.kobeac.resolveCustomers(rows);
        // 顧客別データの初期表示も ID 昇順 (全データは基底クラスで設定)。列見出しで並べ替え可能
        this.detail.sortDescriptions.push(new wjcCore.SortDescription('ID', true));
        this.engine = this.buildEngine();
    }

    async ngOnInit() {
        await this.kobeac.ensureLoaded();
        this.initMaps();
        if (this.pendingAuctionRid) {
            // 埋め込み時: 親から渡された日付を適用する。
            this.applyAuction(this.pendingAuctionRid);
            this.pendingAuctionRid = null;
        } else if (!this.embedded && this.auctions.length) {
            // 単独ルート時のみ既定で最新日付を選ぶ (埋め込み時は親が auctionRid で駆動)。
            this.current = this.auctions[0];
            this.onAuctionChanged();
        }
        // detail データが揃ったら帳票データを描画可能としてマークする。実際の描画は
        // 売買台帳タブがアクティブ (#summaryViewer が DOM に生成) になったのを
        // ngAfterViewChecked で検知して行う (ngx-bootstrap は非アクティブタブを DOM 除去)。
        this.detail.querySucceeded.addHandler(() => {
            this.reportReady = true;
            this.ledgerRendered = false;
        });
        // 明細を取得し直したら集計も計算し直す (全データタブのグリッドが描画されていなくても反映する)
        this.items.querySucceeded.addHandler(() => {
            this.calculateTotalAll();
            this.refreshing = false;
        });
        this.items.queryFailed.addHandler(() => {
            this.refreshing = false;
        });
    }

    /** 「集計を更新」実行中は true (ボタンを無効化)。 */
    refreshing = false;

    /**
     * 精算書の集計を最新のデータで作り直す。顧客・オークション (参加費/消費税) のマスタを読み直し、
     * 対象オークションの明細を再取得して集計を再計算する。顧客を選択中なら顧客別データと
     * 売買台帳 (帳票) も取り直す。
     */
    async refreshSummary(): Promise<void> {
        if (!wjcCore.isObject(this.current) || this.refreshing) return;
        this.refreshing = true;
        try {
            await this.kobeac.reload();
            this.initMaps();
            const rid = this.current['@rid'];
            const latest = this.auctions.filter((a) => a['@rid'] === rid);
            if (latest.length) this.current = latest[0];
            // 参加費/参加費2/消費税を最新のオークションから取り直す
            this.onAuctionChanged();
            if (wjcCore.isObject(this.customer)) {
                const crid = this.customer['@rid'];
                const c = this.kobeac.customers.filter((x) => x['@rid'] === crid);
                if (c.length) this.customer = c[0];
                this.reportReady = false;
                this.ledgerRendered = false;
                this.detail.cancelChanges();
            }
            // 明細を再取得 (querySucceeded で集計を再計算し refreshing を解除する)
            this.items.cancelChanges();
        } catch (e) {
            this.refreshing = false;
            throw e;
        }
    }

    /** detail データが揃い帳票を描画できる状態か */
    reportReady = false;
    /** 現在の顧客について帳票を描画済みか (顧客変更で false に戻す) */
    private ledgerRendered = false;

    ngAfterViewChecked(): void {
        // 売買台帳タブがアクティブになると #summaryViewer が DOM に現れる。
        // reportReady かつ未描画なら 1 回だけ描画する。
        if (this.reportReady && !this.ledgerRendered && document.getElementById('summaryViewer') != null) {
            this.ledgerRendered = true; // 再入防止 (ngAfterViewChecked は高頻度)
            this.renderReport();
        }
    }

    private buildEngine(): wjcOlap.PivotEngine {
        const engine = new wjcOlap.PivotEngine({
            autoGenerateFields: false,
            itemsSource: this.summaries,
            showColumnTotals: wjcOlap.ShowTotals.GrandTotals,
            showRowTotals: wjcOlap.ShowTotals.GrandTotals,
            fields: [
                {binding: '顧客ID', header: '顧客ID', width: 50},
                {binding: '顧客名', header: '顧客名', width: 200},
                {binding: '売買種類', header: '売買種類'},
                {binding: '金額', header: '金額'},
                {binding: '歩銭', header: '歩銭'},
                {binding: '消費税', header: '消費税'},
                {binding: '合計', header: '合計'},
            ],
        });
        engine.rowFields.push('顧客ID', '顧客名');
        engine.columnFields.push('売買種類');
        engine.valueFields.push('金額', '歩銭', '消費税', '合計');
        // 旧 kobe-ac.app (Wijmo 5.20162) の showZeros=true は「0 の集計値を隠さない」で、該当データの無いセルは空欄だった。
        // 現行 Wijmo の true は該当データの無いセルまで 0 で埋めるため、旧と同じ表示・出力になる false にする
        engine.showZeros = false;
        return engine;
    }

    override onAuctionChanged(): void {
        super.onAuctionChanged();
        if (wjcCore.isObject(this.current)) {
            if (wjcCore.isNumber(this.current['参加費'])) this.entrance = this.current['参加費'];
            if (wjcCore.isNumber(this.current['参加費2'])) this.entrance2 = this.current['参加費2'];
        }
    }

    onItemsLoaded(): void {
        this.calculateTotalAll();
    }

    private afterDate(text: string): boolean {
        if (!this.current) return false;
        const cur = new Date(String(this.current['日付']).replace(' ', 'T'));
        return cur >= new Date(text);
    }

    calculateTotalAll(): void {
        if (!wjcCore.isObject(this.current)) return;
        const totalAll: any = {};
        for (const item of this.items.items) {
            if (!wjcCore.isObject(item['売主']) || !wjcCore.isObject(item['買主'])) continue;
            // seller side
            let sellerRid = item['売主']['@rid'];
            if (!totalAll[sellerRid]) totalAll[sellerRid] = {customer: item['売主'], summary: {}};
            let summary = totalAll[sellerRid].summary;
            let field = '売' + item['売歩'] + '%';
            if (item['個数'] < 0) field = '売返' + item['買歩'] + '%';
            if (summary[field]) {
                summary[field]['金額'] = summary[field]['金額'] + item['個数'] * item['単価'] * -1;
            } else {
                summary[field] = {掛率: item['個数'] < 0 ? item['買歩'] : item['売歩'], 金額: item['個数'] * item['単価'] * -1};
            }
            if (item['個数'] > 0 && item['売歩'] > 0 && this.entrance > 0) {
                summary['参加費'] = {掛率: 0, 金額: this.entrance};
                if (item['売主']['参加費不要']) delete summary['参加費'];
            }
            if (item['個数'] > 0 && item['売歩'] > 0 && this.entrance2 > 0 && String(item['売主']['名称']).indexOf('▲') >= 0) {
                summary['参加費2'] = {掛率: 0, 金額: this.entrance2};
            }
            // buyer side
            let buyerRid = item['買主']['@rid'];
            if (!totalAll[buyerRid]) totalAll[buyerRid] = {customer: item['買主'], summary: {}};
            summary = totalAll[buyerRid].summary;
            field = '買' + item['買歩'] + '%';
            if (item['個数'] < 0) field = '買返' + item['買歩'] + '%';
            if (summary[field]) {
                summary[field]['金額'] = summary[field]['金額'] + item['個数'] * item['単価'];
            } else {
                summary[field] = {掛率: item['個数'] < 0 ? item['買歩'] * -1 : item['買歩'], 金額: item['個数'] * item['単価']};
            }
            if (item['個数'] > 0 && item['買歩'] > 0 && this.entrance > 0) {
                summary['参加費'] = {掛率: 0, 金額: this.entrance};
                if (item['買主']['参加費不要']) delete summary['参加費'];
            }
        }

        const summaries: any[] = [];
        Object.keys(totalAll).forEach((key) => {
            const customer = totalAll[key].customer;
            const summary = totalAll[key].summary;
            Object.keys(summary).forEach((k) => {
                const v = summary[k];
                v['顧客'] = customer;
                v['顧客ID'] = customer['ID'];
                v['顧客名'] = customer['名称'];
                v['売買種類'] = k;
                if (k.indexOf('売') > -1) {
                    v['相手'] = '買方';
                    if (this.afterDate('2019-10-01') && k.indexOf('売返') == -1 && v['金額'] > 0) {
                        v['歩銭'] = Math.floor(v['金額'] * -1 * v['掛率'] / 100);
                    } else {
                        v['歩銭'] = Math.floor(Math.abs(v['金額']) * v['掛率'] / 100);
                    }
                } else {
                    v['相手'] = '売方';
                    if (this.afterDate('2019-10-01') && k.indexOf('買返') == -1 && v['金額'] < 0) {
                        v['歩銭'] = Math.floor(v['金額'] * v['掛率'] / 100);
                    } else {
                        v['歩銭'] = Math.floor(Math.abs(v['金額']) * v['掛率'] / 100);
                    }
                }
                v['消費税'] = Math.floor(v['歩銭'] * this.tax / 100);
                v['合計'] = v['金額'] + v['歩銭'] + v['消費税'];
                summaries.push(v);
            });
        });
        this.summaries = new wjcCore.CollectionView(summaries);
        this.engine = this.buildEngine();
        this.engine.itemsSource = this.summaries;
    }

    onSummarySelectionChanged(): void {
        // Wijmo Angular の (selectionChanged) の $event はイベント引数側で selectedItems を
        // 持たない。selectedItems はグリッド (sender) にあるため ViewChild 参照から読む。
        const grid: any = this.summaryGrid;
        if (!wjcCore.isObject(this.current) || grid == null || !wjcCore.isArray(grid.selectedItems) || grid.selectedItems.length == 0) return;
        const obj = grid.selectedItems[0];
        if (wjcCore.isObject(obj.$rowKey) && wjcCore.isObject(obj.$rowKey._item) && obj.$rowKey._item['顧客']) {
            this.customer = obj.$rowKey._item['顧客'];
            // 顧客が変わったので帳票を再描画対象にする (detail ロード完了で reportReady=true)
            this.reportReady = false;
            this.ledgerRendered = false;
            this.detail.filterSpecial = '`オークション` = ' + this.current['@rid'] +
                ' AND (`売主` = ' + this.customer['@rid'] + ' OR `買主` = ' + this.customer['@rid'] + ')';
            this.detail.filterOnServer = true;
            // 統合画面へ選択顧客を通知 (請求編集タブが同じ顧客を読み込む)。
            if (this.customer['@rid']) {
                this.customerSelected.emit(this.customer['@rid']);
            }
        }
    }

    canPrint(): boolean {
        return wjcCore.isObject(this.customer);
    }

    print(): void {
        if (this.viewer && this.viewer.report) this.viewer.report.print();
    }

    private async renderReport(): Promise<void> {
        if (!wjcCore.isObject(this.customer)) return;
        // 描画先 (売買台帳タブ内) が DOM に無ければ描画しない (タブ選択時に再実行される)
        if (document.getElementById('summaryViewer') == null) return;
        await this.fontService.ensureLoaded();
        const options = new Stimulsoft.Viewer.StiViewerOptions();
        options.appearance.scrollbarsMode = false;
        this.viewer = new Stimulsoft.Viewer.StiViewer(options, 'StiViewer', false);
        this.viewer.renderHtml('summaryViewer');
        this.dataService.query({
            sql: "SELECT FROM AC_Report WHERE name = '売買台帳'",
            count: "SELECT FROM AC_Report WHERE name = '売買台帳'",
            fetchPlan: '*:0',
        }, ActivePromotionConfig.SERVER_URL).subscribe((response: any) => {
            if (!wjcCore.isArray(response.results) || response.results.length == 0) return;
            const json = typeof response.results[0].content == 'string'
                ? JSON.parse(response.results[0].content) : Object.assign({}, response.results[0].content);
            delete json['@type'];
            delete json['@version'];
            const report = new Stimulsoft.Report.StiReport();
            report.load(json);
            report.dictionary.databases.clear();

            const data: any = {auction: {日付: this.kobeac.formatOrientDate(this.current['日付']), 合計金額: 0}, summary: [], detail: []};
            for (const summary of this.summaries.items) {
                if (summary['顧客']['@rid'] == this.customer['@rid']) data.summary.push(summary);
            }
            for (const item of this.detail.items) {
                const detail: any = {ID: item.ID};
                const isSeller = item['売主'] && item['売主']['@rid'] == this.customer['@rid'];
                const isBuyer = item['買主'] && item['買主']['@rid'] == this.customer['@rid'];
                if (isSeller) {
                    detail['売買種類'] = item['個数'] < 0 ? ('売返' + item['買歩'] + '%') : ('売' + item['売歩'] + '%');
                    detail['金額'] = this.afterDate('2019-10-01') ? Math.abs(item['個数']) * item['単価'] : Math.abs(item['個数'] * item['単価']);
                    if (Math.abs(item['個数']) > 1) detail['付加情報'] = Math.abs(item['個数']) + ' x ' + item['単価'];
                    detail['商品名'] = item['商品名'];
                    detail['相手'] = item['買主'] ? item['買主']['名称'] : '';
                }
                if (isBuyer) {
                    detail['売買種類'] = item['個数'] < 0 ? ('買返' + item['買歩'] + '%') : ('買' + item['買歩'] + '%');
                    detail['金額'] = this.afterDate('2019-10-01') ? Math.abs(item['個数']) * item['単価'] : Math.abs(item['個数'] * item['単価']);
                    if (Math.abs(item['個数']) > 1) detail['付加情報'] = Math.abs(item['個数']) + ' x ' + item['単価'];
                    detail['商品名'] = item['商品名'];
                    detail['相手'] = item['売主'] ? item['売主']['名称'] : '';
                }
                if (this.afterDate('2019-10-01') || detail['金額'] > 0) data.detail.push(detail);
            }
            data.customer = Object.assign({}, this.customer);
            for (const item of data.summary) data.auction['合計金額'] = data.auction['合計金額'] + item['合計'];
            const dataSet = new Stimulsoft.System.Data.DataSet('data');
            dataSet.readJson(data);
            report.regData(dataSet.dataSetName, 'data', dataSet);
            this.viewer.report = report;
        });
    }
}
